import React, { useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { PieChart, Pie, Cell } from "recharts";

const POOL_GREEN = "#26890d";
const OVERAGE_AMBER = "#e53835";
const NO_POOL_GREY = "#d8d8d8";

const formatUSD = (v) =>
    "$" + v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatAIC = (v) =>
    v.toLocaleString("en-AU", { maximumFractionDigits: 0 }) + " AIC";

const LegendDot = ({ color, label, value }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
            sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: color,
                flexShrink: 0,
            }}
        />
        <Typography sx={{ fontSize: "0.78rem", color: "#555" }}>
            {label}
        </Typography>
        {value !== undefined && (
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#1a1a1a", ml: "auto" }}>
                {value}
            </Typography>
        )}
    </Box>
);

const PoolPieChart = ({ data }) => {
    const {
        total_pool_covered_credits,
        total_overage_credits,
        pool_utilization_pct,
        total_pool_savings_amount,
        total_overage_amount,
    } = data;

    const [activeIndex, setActiveIndex] = useState(null);

    const pieData = [
        ...(total_pool_covered_credits > 0
            ? [{ name: "Pool Covered", value: total_pool_covered_credits, fill: POOL_GREEN }]
            : []),
        ...(total_overage_credits > 0
            ? [{ name: "Overage", value: total_overage_credits, fill: OVERAGE_AMBER }]
            : []),
    ];

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: "12px",
                border: "1px solid #e8e8e8",
                backgroundColor: "#fff",
                padding: "24px",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                boxSizing: "border-box",
            }}
        >
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#1a1a1a", mb: 0.5 }}>
                Pool Consumption vs Overage
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#999", mb: 2.5 }}>
                Credits covered by pool against excess usage
            </Typography>

            {/* Donut + centre label */}
            <Box
                sx={{
                    position: "relative",
                    width: 200,
                    height: 200,
                    alignSelf: "center",
                    mb: 2.5,
                    "& svg, & svg *": {
                        outline: "none",
                    },
                }}
            >
                <PieChart width={200} height={200} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <Pie
                        data={pieData}
                        cx={100}
                        cy={100}
                        innerRadius={62}
                        outerRadius={92}
                        dataKey="value"
                        startAngle={90}
                        endAngle={-270}
                        paddingAngle={pieData.length > 1 ? 3 : 0}
                        strokeWidth={0}
                        onMouseEnter={(_, index) => setActiveIndex(index)}
                        onMouseLeave={() => setActiveIndex(null)}
                    >
                        {pieData.map((entry, index) => (
                            <Cell
                                key={entry.name}
                                fill={entry.fill}
                                opacity={activeIndex === null || activeIndex === index ? 1 : 0.45}
                            />
                        ))}
                    </Pie>
                </PieChart>

                {/* Centre label overlay */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        pointerEvents: "none",
                        width: 110,
                    }}
                >
                    {activeIndex !== null ? (
                        <>
                            <Typography
                                sx={{
                                    fontSize: "0.72rem",
                                    fontWeight: 700,
                                    color: pieData[activeIndex].fill,
                                    lineHeight: 1,
                                    mb: 0.4,
                                }}
                            >
                                {pieData[activeIndex].name}
                            </Typography>
                            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#1a1a1a", lineHeight: 1 }}>
                                {formatAIC(pieData[activeIndex].value)}
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: "#1a1a1a", lineHeight: 1 }}>
                                {`${pool_utilization_pct}%`}
                            </Typography>
                            <Typography sx={{ fontSize: "0.8rem", fontWeight: "bold", color: "#888", mt: 0.4 }}>
                                covered
                            </Typography>
                        </>
                    )}
                </Box>
            </Box>

            {/* Legend */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, mb: 2.5, px: 1 }}>
                <LegendDot color={POOL_GREEN} label="Pool Covered" />
                <LegendDot color={OVERAGE_AMBER} label="Overage" />
            </Box>

            {/* Savings / Overage amounts */}
            <Box
                sx={{
                    mt: "auto",
                    borderTop: "1px solid #f0f0f0",
                    pt: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography sx={{ fontSize: "0.78rem", color: "#555" }}>Pool Savings</Typography>
                    <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: POOL_GREEN }}>
                        {formatUSD(total_pool_savings_amount)}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography sx={{ fontSize: "0.78rem", color: "#555" }}>Overage Billed</Typography>
                    <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: OVERAGE_AMBER }}>
                        {formatUSD(total_overage_amount)}
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
};

export default PoolPieChart;
